import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Mail, 
  Send,
  Eye,
  Check,
  X,
  Loader2,
  Settings,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

function AdminNotifications() {
  const { user } = useAuth();
  const [milestones, setMilestones] = useState([]);
  const [emailStatus, setEmailStatus] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [selectedMilestone, setSelectedMilestone] = useState('');
  const [selectedBooking, setSelectedBooking] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [pingEmail, setPingEmail] = useState('');
  const [pinging, setPinging] = useState(false);

  async function handlePing() {
    setPinging(true);
    try {
      const res = await api.post('/admin/email/ping', { recipient_email: pingEmail || null });
      if (res.data.status === 'sent') toast.success('Test email sent to ' + res.data.recipient);
      else toast.warning(res.data.reason || 'Email not sent');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the test email');
    } finally {
      setPinging(false);
    }
  }

  useEffect(function() {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [milestonesRes, statusRes, bookingsRes] = await Promise.all([
        api.get('/admin/email/milestones'),
        api.get('/admin/email/status'),
        api.get('/bookings')
      ]);
      setMilestones(milestonesRes.data.milestones);
      setEmailStatus(statusRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  }

  async function handlePreview() {
    if (!selectedMilestone) {
      toast.error('Please select a milestone');
      return;
    }

    try {
      const response = await api.post('/admin/email/preview', {
        milestone: selectedMilestone,
        booking_id: selectedBooking || null
      });
      setPreviewSubject(response.data.subject);
      setPreviewHtml(response.data.html_preview);
    } catch (err) {
      toast.error('Failed to generate preview');
    }
  }

  async function handleSendTest() {
    if (!selectedMilestone || !selectedBooking) {
      toast.error('Please select a milestone and booking');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/admin/email/test', {
        milestone: selectedMilestone,
        booking_id: selectedBooking,
        recipient_email: testEmail || null
      });
      
      if (response.data.status === 'sent') {
        toast.success(`Test email sent to ${response.data.recipient}`);
      } else if (response.data.status === 'skipped') {
        toast.warning(response.data.message);
      } else {
        toast.error('Failed to send test email');
      }
    } catch (err) {
      toast.error('Failed to send test email');
    } finally {
      setSending(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Email notifications</h1>
          <p className="text-gray-600 mt-1">Configure and test shipment milestone notifications</p>
        </div>
      </div>

      {/* Email delivery — live Resend status */}
      <div className={"rounded-sm p-6 border " + (emailStatus?.mode === 'verified' ? "bg-green-50 border-green-200" : emailStatus?.configured ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-200")} data-testid="email-delivery-card">
        <div className="flex items-start gap-4">
          {emailStatus?.mode === 'verified' ? <CheckCircle2 className="h-6 w-6 text-green-600 mt-1" /> : <AlertCircle className="h-6 w-6 text-amber-600 mt-1" />}
          <div className="flex-1">
            <h3 className={"font-medium text-lg " + (emailStatus?.mode === 'verified' ? "text-green-800" : "text-amber-800")} data-testid="email-delivery-mode">
              {emailStatus?.mode === 'verified' ? "Email delivery live — verified domain" : emailStatus?.mode === 'test' ? "Email in Resend test mode" : emailStatus?.mode === 'unverified' ? "Sender domain not verified" : "Email service not configured"}
            </h3>
            <p className="text-sm mt-1 text-gray-700">
              {emailStatus?.mode === 'verified' && `Documents go out from ${emailStatus.sender_email}; status notifications from ${emailStatus.notification_sender_email}. Replies go to ${emailStatus.reply_to || 'the sender'}.`}
              {emailStatus?.mode === 'test' && `Sender ${emailStatus.sender_email} only delivers to the address of your own Resend account. Verify sudvik.com in Resend and set SENDER_EMAIL to a sudvik.com address.`}
              {emailStatus?.mode === 'unverified' && `Sender ${emailStatus.sender_email} uses a domain that Resend has not verified yet. Add the DNS records shown in Resend → Domains.`}
              {!emailStatus?.configured && "Add RESEND_API_KEY to backend/.env to enable email notifications."}
            </p>
            {emailStatus?.domains?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="email-domains">
                {emailStatus.domains.map(function(d) {
                  return <span key={d.name} className={"text-xs px-2.5 py-1 rounded-full sv-mono " + (d.status === 'verified' ? 'bg-white text-green-800 border border-green-300' : 'bg-white text-amber-800 border border-amber-300')}>{d.name} · {d.status}{d.region ? ' · ' + d.region : ''}</span>;
                })}
              </div>
            )}
            {emailStatus?.domains_error && <p className="text-xs text-gray-600 mt-2">Domain status unavailable: {emailStatus.domains_error}</p>}
            {emailStatus?.configured && (
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2">
                <Input value={pingEmail} onChange={function(e) { setPingEmail(e.target.value); }} placeholder={user?.email || 'you@company.com'} className="h-10 rounded-sm sm:w-72 bg-white" data-testid="email-ping-recipient" />
                <Button onClick={handlePing} disabled={pinging} variant="outline" className="h-10 rounded-sm bg-white" data-testid="email-ping-btn">{pinging ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send test email to me</>}</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {emailStatus?.recent_document_emails?.length > 0 && (
        <div className="bg-white rounded-sm shadow-card p-6" data-testid="recent-document-emails">
          <h3 className="font-medium text-ink mb-3">Recent document e-mails</h3>
          <div className="divide-y divide-gray-100 text-sm">
            {emailStatus.recent_document_emails.slice(0, 10).map(function(r, i) {
              return (
                <div key={i} className="py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span className="sv-mono text-ink">{r.booking_number}</span>
                  <span className="text-gray-700">{r.document.replace(/-/g, ' ')}</span>
                  <span className="text-gray-500 truncate max-w-md">{[...(r.to || []), ...(r.cc || [])].join(', ') || 'no recipient'}</span>
                  <span className={"ml-auto text-xs px-2 py-0.5 rounded-full " + (r.status === 'sent' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800')} title={r.reason || ''}>{r.status}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Milestone Types */}
      <div className="bg-white rounded-sm shadow-card p-6">
        <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
          <Mail className="h-5 w-5 text-blue-600" />
          Email Milestones
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {milestones.map(function(milestone) {
            return (
              <div 
                key={milestone.key} 
                className={"p-4 rounded-sm border-2 transition-all cursor-pointer " + 
                  (selectedMilestone === milestone.key 
                    ? "border-blue-500 bg-blue-500/5" 
                    : "border-gray-100 hover:border-gray-200")}
                onClick={function() { setSelectedMilestone(milestone.key); }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-2xl">{milestone.icon}</span>
                  <span className="font-medium text-ink">{milestone.name}</span>
                </div>
                <p className="text-sm text-gray-500">{milestone.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview & Test Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Controls */}
        <div className="bg-white rounded-sm shadow-card p-6">
          <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-600" />
            Preview & Test
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="font-medium text-gray-700">Selected milestone</Label>
              <Input 
                value={milestones.find(function(m) { return m.key === selectedMilestone; })?.name || 'None selected'} 
                disabled 
                className="mt-1 bg-gray-50"
              />
            </div>

            <div>
              <Label className="font-medium text-gray-700">Select Booking (for real data)</Label>
              <Select value={selectedBooking || "sample"} onValueChange={function(val) { setSelectedBooking(val === "sample" ? "" : val); }}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Use sample data" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sample">Use sample data</SelectItem>
                  {bookings.map(function(booking) {
                    return (
                      <SelectItem key={booking.id} value={booking.id}>
                        {booking.booking_number} - {booking.origin_port.split(',')[0]} → {booking.destination_port.split(',')[0]}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="font-medium text-gray-700">Test Email Recipient (optional)</Label>
              <Input 
                type="email"
                placeholder="Override recipient email"
                value={testEmail}
                onChange={function(e) { setTestEmail(e.target.value); }}
                className="mt-1"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                onClick={handlePreview}
                disabled={!selectedMilestone}
                className="flex-1 bg-ink hover:bg-ink-800"
              >
                <Eye className="h-4 w-4 mr-2" />
                Preview Email
              </Button>
              <Button 
                onClick={handleSendTest}
                disabled={!selectedMilestone || !selectedBooking || sending}
                className="flex-1 bg-ink hover:bg-ink-800"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Send Test
              </Button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-sm shadow-card p-6">
          <h2 className="text-lg font-medium text-ink mb-4 flex items-center gap-2">
            <Eye className="h-5 w-5 text-blue-600" />
            Email Preview
          </h2>
          
          {previewHtml ? (
            <div>
              <div className="mb-3 p-3 bg-gray-50 rounded-sm">
                <p className="text-xs text-gray-500 mb-1">Subject:</p>
                <p className="font-medium text-ink">{previewSubject}</p>
              </div>
              <div className="border rounded-sm overflow-hidden" style={{height: '400px'}}>
                <iframe 
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-full"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <Mail className="h-12 w-12 mb-3" />
              <p>Select a milestone and click Preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminNotifications;
